import { CallbacksManager } from '~spa/Utils/CallbacksManager';

import { WsBaseInterface } from '~shared/websockets/interface/WsBaseInterface';
import webSocketSubscriptionStore from '~shared/websockets/utils/WsSubscriptionStore';
import sbLiveDataSubscriptionStore from '~shared/websockets/sbLiveDataUtils/WsSbLiveDataSubscriptionStore';
import { sldGenericSubscriptionStoreMiddleware } from '~shared/websockets/sbLiveDataUtils/middleware/sldGenericSubscriptionStoreMiddleware';
import { sldLoginFlowCheckMiddleware } from '~shared/websockets/sbLiveDataUtils/middleware/sldLoginFlowCheckMiddleware';
import { sldReconnectPayloadFormatter } from '~shared/websockets/sbLiveDataUtils/sbLiveDataUtils';

import { getRegulationTypeId, getLanguageCode } from '~shared/utils/user';

const {
    subscribe: subscribeToStore,
    unsubscribe: unsubscribeFromStore,
    getRegistrationPayload,
    clearSubscriptionStores,
} = webSocketSubscriptionStore;

class WsInterface extends WsBaseInterface {
    #msgCallbacks = new Map();
    #killCallbacks = new CallbacksManager();

    constructor(config) {
        super(config);
        this.addOnMessageCallback(
            (msg) => this.messageReceivedHandler(msg)
        );
    }

    messageReceivedHandler(message) {
        const { action } = message;

        this.#msgCallbacks.get(action)?.runAllCallbacks(message);
    }

    onMessage(action, callback) {
        if (!this.#msgCallbacks.has(action)) {
            this.#msgCallbacks.set(action, new CallbacksManager());
        }

        return this.#msgCallbacks.get(action).addCallback(callback);
    }

    onConnection(callback) {
        return this.addOnOpenCallback(callback);
    }

    onConnectionClose(callback) {
        return this.addOnCloseCallback(callback);
    }

    onKill(callback) {
        this.#killCallbacks.addCallback(callback);
    }

    kill() {
        this.#msgCallbacks.clear();
        this.#killCallbacks.runAllCallbacks();
        this.offConnectionCallbacks();
        this.disconnect();
    }
}

export class GeneralWsInterface extends WsInterface {
    constructor(config) {
        super(config);

        this.onConnection(() => {
            const payload = getRegistrationPayload();
            if (payload.length) {
                this.register(payload);
            }
        });

        this.onKill(clearSubscriptionStores);
    }

    subscribe(subscriptionData) {
        const { registerPayload } = subscribeToStore(
            subscriptionData
        );

        if (registerPayload.length) {
            this.register(registerPayload);
        }
    }

    unsubscribe(unsubscribeData) {
        const { registerPayload, unregisterPayload } = unsubscribeFromStore(
            unsubscribeData
        );

        if (unregisterPayload.length) {
            this.unregister(unregisterPayload);
        }

        if (registerPayload.length) {
            this.register(registerPayload);
        }
    }

    register(data) {
        const payload = {
            action: 'register',
            data,
            lang: getLanguageCode(),
            regulation_type_id: getRegulationTypeId(),
        };

        this.send(payload);
    }

    unregister(data) {
        this.send({
            action: 'unregister',
            data,
        });
    }
}

/**
 * Class to handle the sportsbook_live_data WebSocket connection, including registration and running
 * middleware as required.
 * Important - this connection is created/destroyed on login/logout. If that changes then
 * subscribe/unsubscribe logic in BetslipContainer needs changing.
 */
export class SbLiveDataInterface extends WsInterface {
    #shouldRunMiddlewareActions = ['register', 'unregister'];
    #middleWares = {};

    constructor(config) {
        super(config);

        this.setupMiddleware();

        this.onConnection(() => {
            const registrations = sbLiveDataSubscriptionStore.getSubscriptions();
            Object.keys(registrations).forEach(
                type => {
                    const wsMessage = sldReconnectPayloadFormatter(type, registrations[type]);
                    if (wsMessage) {
                        this.send(wsMessage);
                    }
                },
            );
        });

        this.onKill(() => { sbLiveDataSubscriptionStore.clearSubscriptions(); });
    }

    /**
     * Setup any middleware
     * @returns {void}
     */
    setupMiddleware() {
        /**
         * Middleware is executed on the basis of LIFO. This means you generally want the
         * sldGenericSubscriptionStoreMiddleware added first (if using it) before any other middleware
         * can mutate the wsMessage
         */
        this.registerMiddlewares(
            'gtp_betbuilder', // matches type property in wsMessage
            [sldLoginFlowCheckMiddleware, sldGenericSubscriptionStoreMiddleware],
        );

        this.registerMiddlewares(
            'bet_tracking', // matches type property in wsMessage
            [sldGenericSubscriptionStoreMiddleware],
        );
    }

    /**
     * Register middleware functions for a specific message type
     * @param {string} type - The message type to register middleware for
     * @param {Array<Function>} funcs - Array of middleware functions to register
     * @returns {void}
     */
    registerMiddlewares(type, funcs) {
        this.#middleWares[type] = [...funcs];
    }

    /**
     * Executes middleware functions for a given WebSocket payload in right-to-left order (LIFO).
     * Each middleware receives the accumulated payload and a context object containing the
     * subscriptionStore . If any middleware returns false, processing stops and false is returned.
     * If no middlewares exist for the payload type, the original payload is returned unchanged.
     * @param {Object} wsPayload - The WebSocket payload to process
     * @param {string} wsPayload.type - The type of the WebSocket message
     * @see client/src/shared/websockets/sbLiveDataUtils/middleware for examples
     * @returns {Object|boolean} The processed payload after all middlewares, or false
     */
    runMiddlewares(wsPayload) {
        const { type } = wsPayload;
        const typeSpecificMw = this.#middleWares?.[type];
        if (!typeSpecificMw?.length) {

            return wsPayload;
        }

        return typeSpecificMw.reduceRight(
            (accWsPayload, mwFunction) => {
                if (!accWsPayload) {

                    return accWsPayload;
                }

                return mwFunction(accWsPayload, { subscriptionStore: sbLiveDataSubscriptionStore });
            },
            wsPayload,
        );
    }

    /**
     * Validates a WebSocket registration message.
     * @param {Object} wsMessage - The WebSocket message object to validate.
     * @param {string} wsMessage.type - The type of the WebSocket message.
     * @param {string} wsMessage.action - The action specified in the message.
     * @param {string} expectedAction - The expected action to match against.
     * @returns {boolean} yay/nay
     */
    validateRegistrationMessage(wsMessage, expectedAction) {
        const { type, action } = wsMessage;
        if (!type || action !== expectedAction) {

            return false;
        }

        return true;
    }

    /**
     * Subscribe for updates
     * @param {Object} wsMessage subscription payload
     * @param {string} wsMessage.type subscription type, e.g. gtp_betbuilder
     * @returns {void}
     */
    subscribe(wsMessage) {
        if (!this.validateRegistrationMessage(wsMessage, 'register')) {

            return;
        }

        const processedMessage = this.#shouldRunMiddlewareActions.includes(wsMessage.action)
            ? this.runMiddlewares(wsMessage)
            : wsMessage;

        if (!processedMessage) {

            return;
        }

        this.send(processedMessage);
    }

    /**
     * Unsubscribe from updates.
     * If param is empty, unsubscribe from everything and set this.#hasActiveSubscriptions to false
     * @param {Object} wsMessage subscription payload
     * @param {string} wsMessage.type subscription type, e.g. gtp_betbuilder, bet_tracking
     * @returns {void}
     */
    unsubscribe(wsMessage) {
        if (!this.validateRegistrationMessage(wsMessage, 'unregister')) {

            return;
        }

        const processedMessage = this.#shouldRunMiddlewareActions.includes(wsMessage.action)
            ? this.runMiddlewares(wsMessage)
            : wsMessage;

        if (!processedMessage) {

            return;
        }

        this.send(processedMessage);
    }
}

export class CashoutWsInterface extends WsInterface {}
